/**
 * Meta tags — the page title, the Open Graph and Twitter cards, and the six
 * favicon slots.
 *
 * ─── The one tab that does not call /admin/* ────────────────────────────────
 * It calls `/game-admin/meta`. The core controller behind `/admin/meta` is
 * `@Public()` + `AdminAccessGuard`, which accepts only `x-api-key` (MAD) and
 * turns away a browser holding a perfectly good ADMIN JWT. The game-owned
 * controller at
 * `backend/src/game/admin/game-meta-admin.controller.ts` re-mounts the exact
 * same routes under the normal JWT guard so this tab can reach them. If this
 * tab 401s or 403s and nothing else does, that file is what to look at.
 *
 * ─── Why uploads are not just file inputs posted with the form ──────────────
 * Each slot validates independently, server-side, by PARSING the image bytes —
 * a 32x32 PNG in the 16x16 slot is rejected with the sizes it actually found.
 * Posting six files at once would collapse six specific messages into one
 * failure, so each slot uploads on its own as soon as a file is chosen, and
 * reports its own outcome.
 *
 * ─── What is deliberately not editable ──────────────────────────────────────
 * `image_width` / `image_height`. The backend derives them from the uploaded
 * file's header and REJECTS them on the bulk-update endpoint, so a form field
 * for either would be a control that always fails. They are shown read-only in
 * the resolved block instead.
 */
import {
  clearMetaIcon, clearMetaImage, getMeta, saveMeta, uploadMetaIcon, uploadMetaImage,
} from '../admin-api';
import { META_ICON_SLOTS, META_TEXT_FIELDS } from '../admin-api.types';
import type { MetaIconSlot, MetaResponse } from '../admin-api.types';
import { errorText, esc, field, readForm, section } from '../admin-kit';
import type { AdminTab } from '../admin-kit';

const TEXT_KEYS = META_TEXT_FIELDS.map((f) => f.key) as readonly string[];

function iconRow(slot: (typeof META_ICON_SLOTS)[number], values: Record<string, string>): string {
  const url = values[slot.key] ?? '';
  return `
    <div class="tg-adm-icon">
      <div class="tg-adm-icon-preview">${url
    ? `<img src="${esc(url)}" alt="${esc(slot.label)}">`
    : '<span>none</span>'}</div>
      <div class="tg-adm-icon-meta">
        <strong>${esc(slot.label)}</strong>
        <span class="tg-adm-hint">${esc(slot.hint)}</span>
        ${url ? `<span class="tg-adm-mono tg-adm-icon-url">${esc(url)}</span>` : ''}
      </div>
      <div class="tg-adm-rowacts">
        <label class="tg-adm-btn">Upload
          <input type="file" accept="${esc(slot.accept)}" data-icon-slot="${esc(slot.key)}" hidden>
        </label>
        <button type="button" class="tg-adm-btn tg-adm-btn-danger" data-act="clear-icon"
          data-slot="${esc(slot.key)}" ${url ? '' : 'disabled'}>Clear</button>
      </div>
    </div>`;
}

function view(meta: MetaResponse | null, error: string): string {
  if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
  if (!meta) return '<p class="tg-adm-empty">Loading…</p>';

  const values = meta.values ?? {};
  const resolved = meta.resolved ?? {};

  return `
    <form class="tg-adm-form" data-form="text">
      ${META_TEXT_FIELDS.map((f) => field(f.key, f.label, values[f.key] ?? '', {
    hint: f.hint,
    long: 'long' in f && f.long === true,
  })).join('')}
      <div class="tg-adm-actions">
        <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save text fields</button>
        <span class="tg-adm-hint">An empty field clears the value and falls back.</span>
      </div>
    </form>

    <h3 class="tg-adm-label">Share image (Open Graph / Twitter)</h3>
    <div class="tg-adm-form">
      <div class="tg-adm-icon">
        <div class="tg-adm-icon-preview tg-adm-icon-preview-wide">${values.image
    ? `<img src="${esc(values.image)}" alt="Share image">`
    : '<span>none</span>'}</div>
        <div class="tg-adm-icon-meta">
          <span class="tg-adm-hint">PNG or JPEG, at least 200x200.</span>
          ${values.image_width
    ? `<span class="tg-adm-hint">Measured at ${esc(values.image_width)}x${esc(values.image_height)}.</span>`
    : '<span class="tg-adm-hint">No measured size — a pasted URL cannot be measured, so og:image:width is not emitted.</span>'}
        </div>
        <div class="tg-adm-rowacts">
          <label class="tg-adm-btn">Upload
            <input type="file" accept="image/png,image/jpeg" data-share-image hidden>
          </label>
          <button type="button" class="tg-adm-btn tg-adm-btn-danger" data-act="clear-image"
            ${values.image ? '' : 'disabled'}>Clear</button>
        </div>
      </div>
    </div>

    <h3 class="tg-adm-label">Icons</h3>
    <div class="tg-adm-form">
      ${META_ICON_SLOTS.map((slot) => iconRow(slot, values)).join('')}
      <span class="tg-adm-hint">Only configured slots are emitted. With none set, the shipped
        /favicon.svg is used instead.</span>
    </div>

    <h3 class="tg-adm-label">What the site serves right now</h3>
    <div class="tg-adm-drawer">
      <div class="tg-adm-kv">
        ${Object.entries(resolved)
    .filter(([, value]) => typeof value !== 'object')
    .map(([key, value]) => `<div><span>${esc(key)}</span><span>${esc(value)}</span></div>`)
    .join('')}
      </div>
    </div>`;
}

export const metaTab: AdminTab = {
  id: 'meta',
  label: 'Meta tags',
  group: 'CMS',

  mount(host, ctx) {
    let live = true;
    let meta: MetaResponse | null = null;
    let error = '';

    const paint = () => {
      host.innerHTML = section(
        'Meta tags',
        'Title, social cards and favicons. Injected into the HTML server-side, because crawlers do not run JavaScript.',
        view(meta, error),
        '<button type="button" class="tg-adm-btn" data-act="reload">Reload</button>',
      );
    };

    async function load(): Promise<void> {
      error = '';
      try {
        const result = await getMeta();
        if (!live) return;
        meta = result;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    }

    async function run(what: string, job: () => Promise<unknown>): Promise<void> {
      try {
        await job();
        if (!live) return;
        ctx.notify(what);
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
      }
      await load();
    }

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
      switch (el?.dataset.act) {
        case 'reload':
          void load();
          break;
        case 'clear-icon':
          void run('Icon cleared.', () => clearMetaIcon(el!.dataset.slot as MetaIconSlot));
          break;
        case 'clear-image':
          void run('Share image cleared.', () => clearMetaImage());
          break;
        default:
          break;
      }
    };

    const onChange = (e: Event) => {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      if (input.dataset.iconSlot) {
        void run(`${input.dataset.iconSlot} uploaded.`, () =>
          uploadMetaIcon(input.dataset.iconSlot as MetaIconSlot, file));
      } else if (input.hasAttribute('data-share-image')) {
        void run('Share image uploaded.', () => uploadMetaImage(file));
      }
    };

    const onSubmit = (e: SubmitEvent) => {
      const form = e.target as HTMLFormElement;
      if (form.dataset.form !== 'text') return;
      e.preventDefault();
      const values = readForm(form);
      const settings: Record<string, string> = {};
      for (const key of TEXT_KEYS) settings[key] = String(values[key] ?? '');
      void run('Meta tags saved.', () => saveMeta(settings));
    };

    host.addEventListener('click', onClick);
    host.addEventListener('change', onChange);
    host.addEventListener('submit', onSubmit as EventListener);

    paint();
    void load();

    return () => {
      live = false;
      host.removeEventListener('click', onClick);
      host.removeEventListener('change', onChange);
      host.removeEventListener('submit', onSubmit as EventListener);
    };
  },
};
